// Desktop/laptop/tablet screen-size zoom fix
// ---------------------------------------------------------------------------
// Several pages (Hub included) use a fixed max-width container (e.g.
// `max-w-[1800px] mx-auto`) so the dashboard always shows its full desktop
// layout. On a screen narrower than that design width the layout used to look
// cramped, because everything is built in fixed pixel sizes for a wide screen.
//
// Fix: when the real window is narrower than DESIGN_WIDTH, the whole page is
// zoomed OUT by the amount that makes it lay out as if the window were
// DESIGN_WIDTH wide. That is mathematically the same as giving the browser more
// CSS pixels to lay the page out in, so every element (big buttons and small
// ones alike) shrinks together and the page looks as it does on a large screen,
// only smaller. The zoom is smooth, not a single fixed step:
//
//     zoom = real width / DESIGN_WIDTH, never more than MAX_ZOOM, never less than MIN_ZOOM
//
//   - a laptop 1440 to 1800 wide: MAX_ZOOM (0.8), exactly as before
//   - a 1366 laptop: about 0.76;  a 1180 / 1148 wide iPad in landscape: about 0.64
//   - below that it stops at MIN_ZOOM (0.6)
//   - 1800 wide or more: no zoom at all
// To make everything a little larger or smaller on small screens, change DESIGN_WIDTH (smaller = bigger text) or MIN_ZOOM.
//
// Who gets it: non-touch devices (laptops/desktops, detected via
// `(hover: hover) and (pointer: fine)`), and touch tablets (iPads) in
// LANDSCAPE. Tablets in portrait, and phones, are left alone; their own
// responsive/mobile layouts are untouched.
//
// The real width is read with media queries, which always report the real
// window width and are not changed by the page's own zoom, so the check can
// never feed back on itself (the zoom it applies cannot change the width it
// measures).
//
// The zoom is also published as the CSS variable --c30-page-zoom, so styles for
// things drawn on the page body (drop-down lists) can cancel it (see v3.css).
// ---------------------------------------------------------------------------
const DESIGN_WIDTH = 1800; // matches the Hub page's `max-w-[1800px]` container
const MAX_ZOOM = 0.8;      // what laptops have always had
const MIN_ZOOM = 0.6;      // never smaller than this
// Below this real width (an iPad) the page is marked data-c30-small on <html>, which v3.css uses for the things that only change on a small screen.
const SMALL_WIDTH = 1280;
// A touch device only counts as a tablet when its shortest screen side (CSS px)
// is at least this — keeps phones on their own mobile layout.
const TABLET_MIN_SHORT_SIDE = 700;
let currentZoom = 1;
let supported = null;
function zoomSupported() {
  if (supported !== null) return supported;
  try {
    supported = typeof document.documentElement.style.zoom !== 'undefined';
  } catch (e) {
    supported = false;
  }
  return supported;
}
function isLaptopOrDesktop() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}
function isTabletInLandscape() {
  const shortSide = Math.min(window.screen.width, window.screen.height);
  if (shortSide < TABLET_MIN_SHORT_SIDE) return false;
  return window.matchMedia('(orientation: landscape)').matches;
}
// The real window width in CSS pixels: the widest min-width the browser says matches.
function realViewportWidth() {
  let lo = 200;
  let hi = 6000;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (window.matchMedia(`(min-width: ${mid}px)`).matches) lo = mid; else hi = mid;
  }
  return lo;
}
function zoomFor(width) {
  return Math.round(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, width / DESIGN_WIDTH)) * 100) / 100;
}
function targetZoom() {
  const width = realViewportWidth();
  const wanted = isLaptopOrDesktop() ? width < DESIGN_WIDTH : isTabletInLandscape();
  return wanted ? zoomFor(width) : 1;
}
function evaluate() {
  if (!zoomSupported()) return;
  document.documentElement.toggleAttribute('data-c30-small', realViewportWidth() < SMALL_WIDTH);
  const target = targetZoom();
  if (Math.abs(target - currentZoom) < 0.005) return;
  const root = document.documentElement;
  if (target === 1) {
    root.style.zoom = '';
    root.style.removeProperty('--c30-page-zoom');
  } else {
    root.style.zoom = String(target);
    root.style.setProperty('--c30-page-zoom', String(target));
  }
  currentZoom = target;
  publishUiBoost();
  // Tell the side bars, which size themselves from the zoom (see V3PrefsContext).
  window.dispatchEvent(new Event('c30:page-zoom'));
}
// Small screens: the parts of the interface that would otherwise end up tiny (the minimised side bars) are drawn larger, up to the size they have on a laptop and a bit
// more. 1 on a laptop or a big screen (no change); about 1.18 for a 1366 wide screen and 1.4 for an iPad. Published as --c30-ui-boost, and read by the side bars' widths.
const UI_BOOST_TARGET = 0.8;  // bring them back up to the size a laptop shows them at
const UI_BOOST_EXTRA = 1.12;  // and a little more, since there is plenty of room
const UI_BOOST_MAX = 1.4;
export function getUiBoost() {
  const zoom = getPageZoom();
  if (zoom >= UI_BOOST_TARGET) return 1;
  return Math.min(UI_BOOST_MAX, Math.round((UI_BOOST_TARGET / zoom) * UI_BOOST_EXTRA * 100) / 100);
}
function publishUiBoost() {
  const boost = getUiBoost();
  const root = document.documentElement;
  if (boost === 1) root.style.removeProperty('--c30-ui-boost');
  else root.style.setProperty('--c30-ui-boost', String(boost));
}
let resizeTimer = null;
function scheduleEvaluate() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(evaluate, 150);
}
// The zoom the page is being shown at right now (1 when no zoom is applied). Read from the page itself, so it is always
// correct even after a window resize has switched the zoom on or off. Used by drag-and-drop, which measures in real
// screen pixels while a zoomed page lays things out in its own smaller pixels.
export function getPageZoom() {
  if (typeof document === 'undefined') return 1;
  const zoom = parseFloat(document.documentElement.style.zoom);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}
let initialized = false;
export function initDesktopZoomFix() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  evaluate();
  window.addEventListener('resize', scheduleEvaluate);
  window.addEventListener('orientationchange', scheduleEvaluate);
}
